import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft, Search, TrendingUp, HelpCircle, ExternalLink,
  Star, DollarSign, Info, RefreshCw, Target, Globe
} from 'lucide-react'
import { Button, Card, CardContent, CardHeader, CardTitle, Badge, Spinner } from '@/components/ui'

interface SerpResult {
  position: number
  title: string
  link: string
  snippet: string
  displayed_link: string
}

interface FeaturedSnippet {
  type: 'paragraph' | 'list' | 'table'
  title: string
  snippet: string
  link: string
  source: string
}

interface KnowledgeGraph {
  title: string
  type: string
  description: string
  source?: string
  image?: string
}

interface AdResult {
  position: number
  title: string
  link: string
  description: string
  displayed_link: string
}

interface PeopleAlsoAsk {
  question: string
  snippet?: string
  link?: string
}

interface SerpData {
  keyword: string
  organic_results: SerpResult[]
  people_also_ask: PeopleAlsoAsk[]
  related_searches: string[]
  featured_snippet?: FeaturedSnippet
  knowledge_graph?: KnowledgeGraph
  ads_top?: AdResult[]
  ads_bottom?: AdResult[]
  total_results?: number
  search_time?: number
  position?: number
  positionMatchType?: 'exact' | 'domain'
  positionMatchedUrl?: string
}

interface ArticleInfo {
  id: string
  title: string
  url: string
  keyword: string
}

export function SerpAnalysisPage() {
  const { id } = useParams<{ id: string }>()
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [article, setArticle] = useState<ArticleInfo | null>(null)
  const [serp, setSerp] = useState<SerpData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchSerpData = async (refresh = false) => {
    if (refresh) setIsRefreshing(true)
    else setIsLoading(true)

    try {
      const response = await fetch(`/api/articles/${id}/serp`)
      if (!response.ok) {
        const data = await response.json() as { error?: string }
        throw new Error(data.error || 'Errore durante il caricamento')
      }
      const data = await response.json() as { article: ArticleInfo; serp: SerpData }
      setArticle(data.article)
      setSerp(data.serp)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    if (id) fetchSerpData()
  }, [id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={() => fetchSerpData()}>Riprova</Button>
      </div>
    )
  }

  if (!article || !serp) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Dati non disponibili</p>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/articles/${id}`}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna all'articolo
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analisi SERP</h1>
            <p className="text-gray-600 mt-1">{article.title}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="info">
                <Search className="w-3 h-3 mr-1" />
                {serp.keyword}
              </Badge>
              {serp.position && (
                <Badge variant={serp.positionMatchType === 'exact' ? 'success' : 'warning'}>
                  <Target className="w-3 h-3 mr-1" />
                  #{serp.position} {serp.positionMatchType === 'domain' ? '(altro URL)' : ''}
                </Badge>
              )}
              {serp.total_results && (
                <span className="text-xs text-gray-500">
                  {serp.total_results.toLocaleString()} risultati
                </span>
              )}
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={() => fetchSerpData(true)}
            isLoading={isRefreshing}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ads Top */}
          {serp.ads_top && serp.ads_top.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-600">
                  <DollarSign className="w-5 h-5" />
                  Annunci sponsorizzati ({serp.ads_top.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg mb-4">
                  La presenza di annunci indica una keyword commerciale con alta competizione.
                </p>
                <div className="space-y-3">
                  {serp.ads_top.slice(0, 3).map((ad, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="warning" className="text-xs">Ad</Badge>
                        <span className="text-xs text-gray-500">{ad.displayed_link}</span>
                      </div>
                      <a
                        href={ad.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-700 hover:underline"
                      >
                        {ad.title}
                      </a>
                      <p className="text-sm text-gray-600 mt-1">{ad.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Featured Snippet */}
          {serp.featured_snippet && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-600">
                  <Star className="w-5 h-5" />
                  Featured Snippet (Posizione 0)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-purple-700 bg-purple-50 p-3 rounded-lg mb-4">
                  Il Featured Snippet appare sopra i risultati organici. Ottimizza il contenuto per conquistarlo.
                </p>
                <div className="border-l-4 border-purple-400 pl-4">
                  <h4 className="font-medium">{serp.featured_snippet.title}</h4>
                  <p className="text-sm text-gray-600 mt-2">{serp.featured_snippet.snippet}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <Globe className="w-3 h-3" />
                    {serp.featured_snippet.source}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Organic Results */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Risultati Organici (Top 10)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {serp.organic_results.slice(0, 10).map((result) => {
                  const isOurUrl = result.link === article.url ||
                    result.link === serp.positionMatchedUrl
                  return (
                    <div
                      key={result.position}
                      className={`p-4 rounded-lg ${
                        isOurUrl
                          ? 'bg-green-50 border-2 border-green-300'
                          : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          result.position <= 3
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {result.position}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-gray-500 truncate">
                              {result.displayed_link}
                            </span>
                            {isOurUrl && (
                              <Badge variant="success" className="text-xs">Il tuo articolo</Badge>
                            )}
                          </div>
                          <a
                            href={result.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-700 hover:underline line-clamp-2"
                          >
                            {result.title}
                          </a>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {result.snippet}
                          </p>
                        </div>
                        <a
                          href={result.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* People Also Ask */}
          {serp.people_also_ask.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  People Also Ask ({serp.people_also_ask.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-blue-700 bg-blue-50 p-3 rounded-lg mb-4">
                  Rispondi a queste domande nel tuo contenuto per aumentare la rilevanza.
                </p>
                <div className="space-y-3">
                  {serp.people_also_ask.map((paa, i) => (
                    <details key={i} className="group">
                      <summary className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <HelpCircle className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">{paa.question}</span>
                      </summary>
                      {paa.snippet && (
                        <div className="mt-2 ml-6 p-3 text-sm text-gray-600 border-l-2 border-blue-200">
                          {paa.snippet}
                        </div>
                      )}
                    </details>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Knowledge Graph */}
          {serp.knowledge_graph && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-indigo-600">
                  <Info className="w-5 h-5" />
                  Knowledge Graph
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-indigo-700 bg-indigo-50 p-3 rounded-lg mb-4">
                  Google considera questa query un'entità. I dati strutturati sono importanti.
                </p>
                <div>
                  <h4 className="font-medium">{serp.knowledge_graph.title}</h4>
                  {serp.knowledge_graph.type && (
                    <Badge variant="info" className="mt-1">{serp.knowledge_graph.type}</Badge>
                  )}
                  <p className="text-sm text-gray-600 mt-2">{serp.knowledge_graph.description}</p>
                  {serp.knowledge_graph.source && (
                    <p className="text-xs text-gray-500 mt-2">
                      Fonte: {serp.knowledge_graph.source}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Related Searches */}
          {serp.related_searches.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Ricerche Correlate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Keyword secondarie da considerare per il contenuto.
                </p>
                <div className="flex flex-wrap gap-2">
                  {serp.related_searches.map((search, i) => (
                    <a
                      key={i}
                      href={`https://www.google.it/search?q=${encodeURIComponent(search)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-700 transition-colors"
                    >
                      {search}
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Statistiche</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Posizione</span>
                  <span className="font-medium">
                    {serp.position ? `#${serp.position}` : 'Non in top 20'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Risultati totali</span>
                  <span className="font-medium">
                    {serp.total_results?.toLocaleString() || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Featured Snippet</span>
                  <span className="font-medium">
                    {serp.featured_snippet ? 'Presente' : 'Assente'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Annunci top</span>
                  <span className="font-medium">
                    {serp.ads_top?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PAA</span>
                  <span className="font-medium">
                    {serp.people_also_ask.length}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
